import { Heart, Hourglass, Languages, Phone } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface Product {
  id: string
  name: string
  railLabel: string
  tagline: string
  audience: string
  /** Opening hook on the card: the moment this product exists for. */
  scenario: string
  description: string
  url: string
  icon: LucideIcon
  color: string
  ctaLabel: string
  status: 'live' | 'coming-soon'
  statusDetail?: string
}

export const products: Product[] = [
  {
    id: 'reeva',
    name: 'Reeva',
    railLabel: 'reeva',
    tagline: 'The AI receptionist that answers every call',
    audience: 'For businesses that cannot afford a missed call',
    scenario: 'Your business phone rings while your hands are full.',
    description: 'Reeva answers every call, books appointments, captures leads, and texts you what happened, 24 hours a day, in your callers’ own language.',
    url: 'https://helloreeva.com',
    icon: Phone,
    color: '#17c964',
    ctaLabel: 'Try Reeva',
    status: 'coming-soon',
    statusDetail: 'Closed beta',
  },
  {
    id: 'saywordy',
    name: 'SayWordy',
    railLabel: 'saywordy',
    tagline: 'Live translation for phone conversations',
    audience: 'For conversations across languages',
    scenario: 'You have something to say. You don’t share a language.',
    description: 'SayWordy translates both sides of a phone conversation so you can speak your own language and understand each other. Start from the web and talk on your own phones. Private beta with US calling.',
    url: 'https://saywordy.com',
    icon: Languages,
    color: '#7c3aed',
    ctaLabel: 'Explore SayWordy',
    status: 'coming-soon',
    statusDetail: 'Private beta',
  },
  {
    id: 'loveline',
    name: 'Loveline',
    railLabel: 'loveline',
    tagline: 'A love letter that talks back',
    audience: 'For people who want to say it better, not later',
    scenario: 'You want to say something that actually lands.',
    description: 'Record a voice letter for someone you love. They get a private phone number to call and hear it in your actual voice, anytime, as a real conversation.',
    url: 'https://getloveline.com',
    icon: Heart,
    color: '#ff4f81',
    ctaLabel: 'Create a Loveline',
    status: 'live',
  },
  {
    id: 'holdforme',
    name: 'Hold For Me',
    railLabel: 'hold for me',
    tagline: 'A robot that is very good at being patient',
    audience: 'For clinics, billers, and anyone stuck in a phone queue',
    scenario: 'You are on hold, again, listening to the same song.',
    description: 'Hold For Me dials, navigates the phone tree, waits out the hold music, and patches you in the moment a human answers. Pay per connect, no seats, no subscription.',
    url: 'https://holdforme.com',
    icon: Hourglass,
    color: '#2e8bff',
    ctaLabel: 'See Hold For Me',
    status: 'coming-soon',
    statusDetail: 'Closed beta',
  },
]

export interface SuiteBrand {
  id: string
  label: string
  url: string
  isExternal: boolean
  isCurrent?: boolean
}

export const suiteBrands: SuiteBrand[] = [
  { id: 'returned', label: 'returned', url: '/', isExternal: false, isCurrent: true },
  { id: 'reeva', label: 'reeva', url: 'https://helloreeva.com', isExternal: true },
  { id: 'saywordy', label: 'saywordy', url: 'https://saywordy.com', isExternal: true },
  { id: 'loveline', label: 'loveline', url: 'https://getloveline.com', isExternal: true },
  { id: 'holdforme', label: 'hold for me', url: 'https://holdforme.com', isExternal: true },
]

// Proof-strip facts. Sources:
// - "18 languages" — Reeva's own landing (products/returned-app/src/components/landing.tsx:56
//   "Speaks 18 languages"); scoped to Reeva in the label so it isn't read as a suite claim.
// - "24/7" — capability phrasing (the receptionist answers whenever the phone rings), no
//   aggregate usage metric claimed.
// - Mission line — Paul's locked brand messaging (2026-08-05).
export const homeProofStats = [
  { id: 'always-on', value: '24/7', label: 'an AI answers, day or night' },
  { id: 'languages', value: '18', label: 'languages Reeva speaks' },
  { id: 'mission', value: '1', label: 'mission: deepen human connection' },
] as const
